import { ReactElement, useState } from 'react';
import clsx from 'clsx';
import moment from 'moment';
import 'moment-timezone';
import Header from './Header';
import ButtonGroup from '../controls/ButtonGroup';
import { Alert } from '../constants/telosair-interfaces';
import { useData } from '../context/data.context';
import { getBgClassNameByAlert, getParameterByName } from '../_helpers/globalHelper';
import Button from '../controls/Button';
import AddNotesModal from '../modals/AddNotes';

type CommentsCompProps = {
  selectedAlert: Alert;
};

/**
 * Notes page renders list of Notes.
 * @param props - Properties of thr component.
 * @returns - ReactElement.
 */
function Comments(props: CommentsCompProps): ReactElement {
  const { selectedAlert } = props;

  const { userConfigData } = useData();
  const timeZone = userConfigData?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (!selectedAlert.notes || selectedAlert.notes.length === 0)
    return <div className="text-center text-gray-500 text-xs my-3">Nothing added yet!</div>;

  return (
    <div className="px-4 max-h-96 overflow-auto">
      {selectedAlert.notes.map((item) => (
        <div className="flex w-full items-start justify-between mt-4 " key={item.id}>
          <div className="max-w-[65%]">
            <div className="items-start gap-3 flex text-gray-300 text-sm break-words">
              <div>&bull; </div>
              <div className="text-left break-words max-w-full">{item.title}</div>
            </div>
            <div className="items-start flex gap-3 text-gray-500 text-xs font-normal w-fit mt-2">
              <div className="invisible">&bull; </div>
              <div className="text-left">{item.addedBy}</div>
            </div>
          </div>
          <div className="text-xs font-medium text-gray-400">
            {moment(item.createdDate).tz(timeZone).format('Do MMM hh:mm a')}
          </div>
        </div>
      ))}
    </div>
  );
}

type AlertCompProps = {
  alerts: Alert[];
};
/**
 * Alerts page renders list of Alerts.
 * @param props - Properties of th component.
 * @returns - ReactElement.
 */
function Alerts(props: AlertCompProps): ReactElement {
  const { alerts } = props;

  const { alertRules, parameters, userConfigData } = useData();
  const timeZone = userConfigData?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (alerts.length === 0) {
    return <div className="text-center text-gray-500 text-xs my-3">0 Alert</div>;
  }
  return (
    <div className="px-4 max-h-96 overflow-auto">
      {alerts.map((alert) => {
        const rule = alertRules.filter((item) => item.id === alert.alertId);
        const parameter = getParameterByName(alert.parameter, parameters);
        return (
          <div key={alert.id} className="flex w-full items-center justify-between mt-3 ">
            <div className="items-center gap-3 flex max-w-[80%] min-h-[4rem]">
              <div
                className={`w-11 min-w-[2.75rem] h-11 ${getBgClassNameByAlert(
                  rule
                )} rounded-full flex items-center justify-center font-bold text-xs text-gray-200 overflow-hidden`}
              >
                {parameter?.shortName || '-'}
              </div>
              <div>
                <div className="text-gray-300 text-sm font-medium text-left break-words">
                  {alert.name}
                </div>
                <div className="justify-start text-gray-500 text-xs font-normal w-fit mt-1">
                  #{alert.serialNumber}{' '}
                  {parameter ? ` | ${parameter.displayName}: ${alert.avgValue}` : '-'}
                </div>
              </div>
            </div>
            <div className="text-xs font-medium text-gray-400">
              {moment(alert.timestamp).tz(timeZone).fromNow()}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * SingleActivity component renders activity.
 * @param props - Properties of single activity component.
 * @returns - ReactElement.
 */
function SingleActivity(props: any): ReactElement {
  const { currentActivity } = props;
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={clsx({ 'dark:bg-gray-900': isOpen })}>
      <div className="flex p-4">
        <div className="rounded-full h-8 w-8 border border-gray-400 dark:border-background-400" />
        <div className=" text-gray-400 dark:text-gray-300 text-sm font-medium ml-4 text-left">
          {currentActivity.actIvityName}
          <p className="text-gray-400 dark:text-gray-400 font-normal text-xs text-start">
            {currentActivity.timeOfActivity}
          </p>
        </div>

        <button
          type="button"
          className="material-icons-outlined text-md cursor-pointer text-gray-500 font-medium ml-auto"
          onClick={() => setIsOpen((prevState) => !prevState)}
        >
          {isOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
        </button>
      </div>
      {isOpen && (
        <div className="text-gray-400 dark:text-gray-300 text-sm font-medium ml-16 py-4 text-left">
          &quot;{currentActivity.description}&quot;
        </div>
      )}
    </div>
  );
}

const ACTIVITIES: any = [
  // {
  //   id: 1,
  //   actIvityName: 'Devices #4242 moved to “Kitchen”',
  //   timeOfActivity: '03:50 PM',
  //   description: 'Identified issue, will be solved within a hour'
  // },
  // {
  //   id: 2,
  //   actIvityName: 'Devices #4242 moved to “Kitchen”',
  //   timeOfActivity: '03:50 PM',
  //   description: 'Identified issue 2, will be solved within a hour'
  // },
  // {
  //   id: 3,
  //   actIvityName: 'Devices #4242 moved to “Kitchen”',
  //   timeOfActivity: '03:50 PM',
  //   description: 'Identified issue 3, will be solved within a hour'
  // },
  // {
  //   id: 4,
  //   actIvityName: 'Devices #4242 moved to “Kitchen”',
  //   timeOfActivity: '03:50 PM',
  //   description: 'Identified issue 4, will be solved within a hour'
  // }
];

/**
 * A component which renders list of Activity.
 * @returns - Activity Component.
 */
function RenderActivity(): ReactElement {
  return (
    <>
      {ACTIVITIES.length === 0 && (
        <div className="text-center text-gray-500 text-xs my-3">0 Activity</div>
      )}
      {ACTIVITIES.map((currentActivity: any) => {
        return <SingleActivity key={currentActivity.id} currentActivity={currentActivity} />;
      })}
    </>
  );
}

type Props = {
  alerts: Alert[];
  selectedAlert: Alert | null;
};

const TABS = [
  { id: 'all-activity', name: 'All Activity' },
  { id: 'alerts', name: 'Alerts' },
  { id: 'notes', name: 'Notes' }
];

/**
 * A DevicesActivity Component renders All the different Tabs.
 * @param props - Properties of the component.
 * @returns - DevicesActivity Component.
 */
export default function DevicesAlertActivity(props: Props): ReactElement {
  const { alerts, selectedAlert } = props;
  const [activeTab, setActiveTab] = useState('all-activity');
  const [showAddNotesModal, setShowAddNotesModal] = useState(false);
  const topDeviceAlerts = alerts.filter(
    (item) => item.serialNumber === selectedAlert?.serialNumber && item.status === 'pending'
  );

  return (
    <>
      <div className="rounded bg-white dark:bg-container-500 shadow-md text-center w-full py-4">
        <div className="px-4">
          <Header
            sectionName="Device Activity"
            subHeading={selectedAlert?.serialNumber && `#${selectedAlert?.serialNumber}`}
          />
        </div>
        {selectedAlert ? (
          <>
            <div className="mt-4 px-4">
              <ButtonGroup
                buttons={TABS}
                size="sm"
                handleClick={setActiveTab}
                activeButton={activeTab}
              />
            </div>
            <div className="my-4">
              {activeTab === 'all-activity' && <RenderActivity />}
              {activeTab === 'notes' && <Comments selectedAlert={selectedAlert} />}
              {activeTab === 'alerts' && <Alerts alerts={topDeviceAlerts.slice(0, 10)} />}
            </div>
            <Button
              onClick={() => setShowAddNotesModal(true)}
              theme="secondary-color"
              size="sm"
              name="Add Notes"
            />
          </>
        ) : (
          <div className="text-center text-gray-500 text-xs my-3">Please select an Alert!</div>
        )}
      </div>
      {selectedAlert && showAddNotesModal && (
        <AddNotesModal alert={selectedAlert} handleClose={() => setShowAddNotesModal(false)} />
      )}
    </>
  );
}
